import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import DefaultLayout from '../layout/DefaultLayout';
import { PricingTable } from '../../shared/types';

export function PricingTablesPage() {
  const [pricingTables, setPricingTables] = useState<PricingTable[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPricingTables = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/pricing-tables', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        let errorMessage = 'Failed to load pricing tables';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          // If JSON parsing fails, try to get the text response
          const errorText = await response.text();
          console.error('Raw error response:', errorText);
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setPricingTables(result.data);
    } catch (error: any) {
      console.error('Error details:', error);
      setError(error.message || 'Error loading pricing tables');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPricingTables();
  }, []);

  const handleDeletePricingTable = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this pricing table?')) {
      try {
        const response = await fetch(`/api/pricing-tables/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to delete pricing table');
        }

        // Success - just refresh the list without showing an alert
        fetchPricingTables(); // Refresh the list
      } catch (error: any) {
        console.error('Error deleting pricing table:', error);
        // Only show error in console, not as browser notification
        console.error(`Error: ${error.message}`);
      }
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      const response = await fetch(`/api/pricing-tables/${id}/toggle-status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to toggle pricing table status');
      }

      // Success - just refresh the list without showing an alert
      fetchPricingTables(); // Refresh the list
    } catch (error: any) {
      console.error('Error toggling pricing table status:', error);
      // Only show error in console, not as browser notification
      console.error(`Error: ${error.message}`);
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString();
  };

  if (isLoading) return (
    <div className="p-4">Loading pricing tables...</div>
  );

  if (error) return (
    <div className="p-4 text-red-500">Error loading pricing tables: {error}</div>
  );

  return (
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Pricing Tables</h1>
          <p className="text-gray-600 mb-4">Manage your pricing tables and versions</p>
          <Link to="/upload-pricing-table">
            <Button>Create New Pricing Table</Button>
          </Link>
        </div>

        <div className="grid gap-6">
          {pricingTables.length > 0 ? (
            pricingTables.map((table) => (
              <Card key={table.id} className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{table.name}</h3>
                    <p className="text-gray-600 mb-2">{table.description}</p>
                    <div className="flex gap-4 text-sm text-gray-500">
                      <span>Created: {formatDate(table.created_at)}</span>
                      <span>Status: {table.is_active ? 'Active' : 'Inactive'}</span>
                      <span>Currency: {table.currency}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Link to="/install">
                      <Button variant="default" size="sm">
                        Install
                      </Button>
                    </Link>
                    <Link to={`/pricing-tables/${table.id}/variations`}>
                      <Button variant="outline" size="sm">
                        View Variations
                      </Button>
                    </Link>
                    <Link to={`/pricing-tables/${table.id}`}>
                      <Button variant="outline" size="sm">
                        View Details
                      </Button>
                    </Link>
                    {table.is_active ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleStatus(table.id)}
                        className="text-red-600 border-red-600 hover:bg-red-50"
                      >
                        Deactivate
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleStatus(table.id)}
                        className="text-green-600 border-green-600 hover:bg-green-50"
                      >
                        Activate
                      </Button>
                    )}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeletePricingTable(table.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <Card className="p-6 text-center text-gray-500">
              <p className="mb-4">No pricing tables found.</p>
              <Link to="/upload-pricing-table">
                <Button>Create Your First Pricing Table</Button>
              </Link>
            </Card>
          )}
        </div>
      </div>
  );
}
